import { useEffect, useState } from 'react';
import { Box, Typography, Card, CardContent, Button, CircularProgress } from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import QRCode from 'qrcode.react';
import { InfoOutlined, AccessTime, Refresh } from '@mui/icons-material';
import PropTypes from 'prop-types';

const UpiPaymentQR = ({ intentDetails }) => {
    const upiUrl = intentDetails?.qr_code_text;
    const TOTAL_TIME = 5 * 60; // 3 minutes in seconds
    const [timeLeft, setTimeLeft] = useState(TOTAL_TIME);
    const [isExpired, setIsExpired] = useState(false);

    useEffect(() => {
        if (timeLeft <= 0) {
            setIsExpired(true);
            // Optionally auto-close or keep open for manual refresh
            // handleCloseModal();
            return;
        }

        const timer = setInterval(() => {
            setTimeLeft((prev) => (prev > 0 ? prev - 1 : 0));
        }, 1000);

        return () => clearInterval(timer);
    }, [timeLeft]);

    const progress = (timeLeft / TOTAL_TIME) * 100;

    return (
        <Box
            component={motion.div}
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, ease: 'easeOut' }}
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                textAlign: 'center',
                py: { xs: 4, md: 6 },
                px: { xs: 2, md: 4 },
                background: 'linear-gradient(135deg, #ffffff 0%, #f0f4f8 100%)',
                borderRadius: 6,
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '4px',
                    background: 'linear-gradient(90deg, #1976d2, #64b5f6)'
                }
            }}
        >
            <Typography
                variant="h4"
                sx={{
                    fontWeight: 800,
                    color: '#0d47a1',
                    mb: 1.5,
                    letterSpacing: '-0.5px'
                }}
            >
                Payment via UPI
            </Typography>

            <Typography variant="body1" sx={{ maxWidth: 500, color: 'text.secondary', mb: 4, lineHeight: 1.6 }}>
                Scan the QR code below using any UPI app like GPay, PhonePe, or Paytm to complete your transaction securely.
            </Typography>

            <AnimatePresence mode="wait">
                {!isExpired ? (
                    <Box
                        key="active-qr"
                        component={motion.div}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        sx={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}
                    >
                        {/* Improved Timer at the Top */}
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 2,
                                mb: 3,
                                py: 1,
                                px: 3,
                                borderRadius: 10,
                                background: 'rgba(25, 118, 210, 0.08)',
                                border: '1px solid rgba(25, 118, 210, 0.12)'
                            }}
                        >
                            <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                                <CircularProgress
                                    variant="determinate"
                                    value={progress}
                                    size={32}
                                    thickness={5}
                                    sx={{ color: timeLeft < 60 ? '#d32f2f' : '#1976d2' }}
                                />
                                <Box
                                    sx={{
                                        top: 0,
                                        left: 0,
                                        bottom: 0,
                                        right: 0,
                                        position: 'absolute',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}
                                >
                                    <AccessTime sx={{ fontSize: 16, color: timeLeft < 60 ? '#d32f2f' : '#1976d2' }} />
                                </Box>
                            </Box>
                            <Typography
                                variant="h6"
                                sx={{
                                    fontWeight: 700,
                                    color: timeLeft < 60 ? '#d32f2f' : '#1976d2',
                                    fontFamily: 'monospace'
                                }}
                            >
                                {timeLeft} seconds left
                            </Typography>
                        </Box>

                        <Card
                            elevation={0}
                            sx={{
                                borderRadius: 5,
                                p: 3,
                                background: '#ffffff',
                                border: '1px solid #e0e6ed',
                                boxShadow: '0 20px 40px rgba(0,0,0,0.06)',
                                transition: 'transform 0.3s ease',
                                '&:hover': {
                                    transform: 'translateY(-5px)',
                                    boxShadow: '0 25px 50px rgba(0,0,0,0.1)'
                                }
                            }}
                        >
                            <CardContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: '0 !important' }}>
                                <Box sx={{ p: 2, background: '#f8fafc', borderRadius: 4, mb: 2 }}>
                                    <QRCode value={upiUrl || ''} size={200} includeMargin={false} renderAs="svg" />
                                </Box>
                                <Typography variant="h5" sx={{ fontWeight: 700, color: '#1e293b', mb: 0.5 }}>
                                    ₹{intentDetails?.amount || '—'}
                                </Typography>
                                <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.secondary' }}>
                                    To: {intentDetails?.name || 'YesPay - Sruthi'}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Box>
                ) : (
                    <Box
                        key="expired-qr"
                        component={motion.div}
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        sx={{
                            py: 4,
                            px: 3,
                            borderRadius: 5,
                            background: '#fff5f5',
                            border: '1px solid #feb2b2',
                            textAlign: 'center',
                            maxWidth: 400
                        }}
                    >
                        <Box sx={{ color: '#c53030', mb: 2 }}>
                            <Refresh sx={{ fontSize: 48 }} />
                        </Box>
                        <Typography variant="h5" color="error" sx={{ fontWeight: 800, mb: 1 }}>
                            Payment Expired
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#742a2a', mb: 3 }}>
                            The session has timed out for security. Please refresh to generate a new QR code.
                        </Typography>
                        <Button
                            variant="contained"
                            fullWidth
                            startIcon={<Refresh />}
                            onClick={() => window.location.reload()}
                            sx={{
                                py: 1.5,
                                borderRadius: 3,
                                textTransform: 'none',
                                fontWeight: 600,
                                boxShadow: '0 4px 12px rgba(25, 118, 210, 0.2)'
                            }}
                        >
                            Generate New QR
                        </Button>
                    </Box>
                )}
            </AnimatePresence>

            <Box
                mt={5}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    color: 'text.secondary',
                    background: '#f8fafc',
                    py: 1.5,
                    px: 3,
                    borderRadius: 4
                }}
            >
                <InfoOutlined color="action" fontSize="small" />
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    Do not close or refresh this page during the process.
                </Typography>
            </Box>
        </Box>
    );
};

UpiPaymentQR.propTypes = {
    intentDetails: PropTypes.object,
    handleCloseModal: PropTypes.func
};

export default UpiPaymentQR;
